import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { MessageSquare, Search, Tag as TagIcon, Settings, UserCheck, UserX, UserPlus, User, Plus, Send, Filter, X, Calendar as CalendarIcon, Instagram, MessageCircle, Globe, Bot, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, isToday, isYesterday } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ChatWithMessages } from "@/types/chat";
import { useState, useEffect, useMemo, useRef } from "react";
import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useOrgMembersQuery } from "@/hooks/useOrgMembersQuery";
import { supabase, chatSupabase} from '@/integrations/supabase/client';
import { GlobalTagsManager } from "@/components/GlobalTagsManager";
import { useTags } from "@/hooks/useTags";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { toast } from "sonner";
import { NewConversationDialog } from "@/components/NewConversationDialog";
import { normalizePhone } from "@/utils/phoneUtils";
import { getDefaultAvatar } from "@/utils/avatarUtils";
import mixpanel from "mixpanel-browser";

interface ChatListProps {
  chats: ChatWithMessages[];
  selectedChat: ChatWithMessages | null;
  onChatSelect: (chat: ChatWithMessages) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  isLoading: boolean;
  contactTags: Record<string, any[]>;
  onUpdateContactTags: (phone: string, tags: any[]) => void;
  refreshTrigger?: number;
  selectedInstance?: string | null;
  onRefetchChats?: () => void;
  /** Chamado após atribuir contato (para invalidar/refetch mensagens e mostrar a mensagem no histórico) */
  onContactAssigned?: () => void;
  /** Pré-carrega mensagens ao passar o mouse sobre o chat */
  onPrefetchChat?: (chat: ChatWithMessages) => void;
  operatorStatus: OperatorStatus;
  onOperatorStatusChange: (status: OperatorStatus) => void;
}

type FilterType = "all" | "assigned" | "unassigned" | "assigned_to_me" | "in_bot";
type StatusFilter = "all" | "em_atendimento" | "fila_humana" | "finalizado";
type DateFilter = "all" | "today" | "this_week" | "this_month" | "custom";
type ClosedFilter = "all" | "open" | "closed";
export type OperatorStatus = "online" | "ausente" | "pausa" | "offline";

const OPERATOR_STATUS_CONFIG: Record<OperatorStatus, { label: string; color: string; ring: string }> = {
  online:  { label: "Online",  color: "#16a34a", ring: "ring-[#16a34a]" },
  ausente: { label: "Ausente", color: "#d97706", ring: "ring-[#d97706]" },
  pausa:   { label: "Pausa",   color: "#ea580c", ring: "ring-[#ea580c]" },
  offline: { label: "Offline", color: "#94a3b8", ring: "ring-[#94a3b8]" },
};

interface AdvancedFilters {
  statusFilter: StatusFilter;
  dateFilter: DateFilter;
  customDate: Date | undefined;
  closedFilter: ClosedFilter;
  selectedTags: string[];
}

function getChatChannelInfo(chat: any) {
  const channel = chat.channel || chat.instance?.channel;
  const integrationType = chat.instance?.integration_type;
  if (channel === "instagram" || integrationType?.includes("INSTAGRAM")) {
    return { name: 'Instagram', icon: Instagram, badgeBg: 'bg-[rgba(147,51,234,0.09)]', badgeText: 'text-[#9333ea]' };
  } else if (channel === 'whatsapp' || integrationType?.includes('WHATSAPP')) {
    return { name: 'WhatsApp', icon: MessageCircle, badgeBg: 'bg-[rgba(22,163,74,0.08)]', badgeText: 'text-[#16a34a]' };
  } else if (channel === 'telegram' || integrationType?.includes('TELEGRAM')) {
    return { name: 'Telegram', icon: MessageSquare, badgeBg: 'bg-[rgba(37,99,235,0.09)]', badgeText: 'text-[#2563eb]' };
  } else if (channel === 'webchat' || integrationType?.includes('WEBCHAT')) {
    return { name: 'WebChat', icon: Globe, badgeBg: 'bg-[rgba(79,70,229,0.09)]', badgeText: 'text-[#4f46e5]' };
  } else if (channel === 'messenger' || integrationType?.includes('MESSENGER')) {
    return { name: 'Messenger', icon: MessageCircle, badgeBg: 'bg-[rgba(37,99,235,0.09)]', badgeText: 'text-[#2563eb]' };
  }
  return null;
}

export function ChatList({
  chats,
  selectedChat,
  onChatSelect,
  searchQuery,
  onSearchChange,
  isLoading,
  contactTags,
  onUpdateContactTags,
  refreshTrigger,
  selectedInstance,
  onRefetchChats,
  onContactAssigned,
  onPrefetchChat,
  operatorStatus,
  onOperatorStatusChange,
}: ChatListProps) {
  const [filterType, setFilterType] = useState<FilterType>("all");

  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({
    statusFilter: "all",
    dateFilter: "all",
    customDate: undefined,
    closedFilter: "all",
    selectedTags: [],
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [showClosedFilter, setShowClosedFilter] = useState(false);
  const [showTagsFilter, setShowTagsFilter] = useState(false);
  const [customDateStart, setCustomDateStart] = useState<Date | undefined>(undefined);
  const [customDateEnd, setCustomDateEnd] = useState<Date | undefined>(undefined);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!searchQuery) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      mixpanel.track('app_chats_searched', { query: searchQuery })
    }, 1000);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current) };
  }, [searchQuery]);
  const [contactsData, setContactsData] = useState<Record<string, { statusatd?: string; assigned_to?: string; name?: string; user_name_insta?: string; assigned_to_name?: string; closed_at?: string; closed_by?: string }>>({});
  const [loadingAssignment, setLoadingAssignment] = useState<string | null>(null);
  const [hasColumnError, setHasColumnError] = useState(false);
  const [showNewConversationDialog, setShowNewConversationDialog] = useState(false);
  const [showClosedChats, setShowClosedChats] = useState(false);
  const { tags } = useTags();
  const { user } = useAuth();
  const { currentOrg } = useOrganizationContext();
  const { isAdmin, canViewAllChats } = useUserPermissions();
  const queryClient = useQueryClient();
  const processedChatsRef = useRef<Set<string>>(new Set());
  const { data: organizationMembers = [] } = useOrgMembersQuery(currentOrg?.id, isAdmin);

  // Dados vêm apenas de chats_Instagram + chat_messages_instagram; carregamento é feito no useEffect [chats, currentOrg]
  // (não há mais carregamento antecipado da tabela contacts)

  // Cache de nomes de perfil por user_id para evitar N+1 no realtime
  const profileNameCacheRef = useRef<Record<string, string>>({});
  const profileNameCacheLimit = 200;

  // Subscription em tempo real para atualizar contatos quando houver mudanças
  const contactsChannelRef = useRef<any>(null);
  useEffect(() => {
    if (!currentOrg?.id) {
      if (contactsChannelRef.current) {
        supabase.removeChannel(contactsChannelRef.current);
        contactsChannelRef.current = null;
      }
      return;
    }

    // Limpar subscription anterior se houver
    if (contactsChannelRef.current) {
      supabase.removeChannel(contactsChannelRef.current);
    }

    // Realtime: apenas chats_Instagram (nada de contacts)
    const handleChatsInstagramChange = async (payload: { new: any }) => {
      const row = payload.new as any;
      const key = row?.origem_contato ?? row?.phone;
      if (!key) return;
      let assignedToName: string | undefined;
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const userId = row.user_atribuido && uuidRegex.test(String(row.user_atribuido).trim()) ? String(row.user_atribuido).trim() : null;
      if (userId) {
        if (profileNameCacheRef.current[userId] !== undefined) {
          assignedToName = profileNameCacheRef.current[userId] || undefined;
        } else {
          const { data: profile } = await supabase.from('profiles').select('name').eq('id', userId).maybeSingle();
          assignedToName = profile?.name;
          if (Object.keys(profileNameCacheRef.current).length < profileNameCacheLimit) {
            profileNameCacheRef.current[userId] = profile?.name ?? '';
          }
        }
      }
      setContactsData(prev => {
        const newData = { ...prev };
        const entry = {
          statusatd: row.status_atd ?? undefined,
          assigned_to: row.user_atribuido ?? undefined,
          name: row.origem_contato ?? key,
          assigned_to_name: assignedToName,
          closed_at: row.closed_at ?? undefined,
          closed_by: row.user_closed_id ?? undefined
        };
        newData[key] = entry;
        const norm = typeof key === 'string' && /^\d/.test(key) ? normalizePhone(key) : key;
        if (norm) newData[norm] = entry;
        return newData;
      });
    };
    contactsChannelRef.current = supabase
      .channel(`chats-instagram-updates-${currentOrg.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'chat',
          table: 'conversations',
          filter: `org_id=eq.${currentOrg.id}`,
        },
        handleChatsInstagramChange
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'chat',
          table: 'conversations',
          filter: `org_id=eq.${currentOrg.id}`,
        },
        handleChatsInstagramChange
      )
      .subscribe();

    return () => {
      if (contactsChannelRef.current) {
        supabase.removeChannel(contactsChannelRef.current);
        contactsChannelRef.current = null;
      }
    };
  }, [currentOrg?.id]);
  

  const formatTime = (dateString: string) => {
    const d = new Date(dateString);
    if (isToday(d))     return format(d, "HH:mm", { locale: ptBR });
    if (isYesterday(d)) return "Ontem";
    if (Date.now() - d.getTime() < 7 * 24 * 60 * 60 * 1000)
      return format(d, "EEE", { locale: ptBR });
    return format(d, "dd/MM", { locale: ptBR });
  };

  const AVATAR_COLORS = [
    { bg: "#dbeafe", text: "#1d4ed8" },
    { bg: "#ede9fe", text: "#6d28d9" },
    { bg: "#dcfce7", text: "#15803d" },
    { bg: "#fef3c7", text: "#b45309" },
    { bg: "#fee2e2", text: "#b91c1c" },
    { bg: "#fce7f3", text: "#9d174d" },
    { bg: "#e0f2fe", text: "#0369a1" },
    { bg: "#f0fdf4", text: "#166534" },
  ];
  const getAvatarColor = (name: string) => {
    const hash = (name || "?").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
  };

  const getInitials = (name: string) => {
    if (!name) return "??";
    // Se for um número, retornar apenas os últimos 2 dígitos
    if (/^\d+$/.test(name)) {
      return name.slice(-2);
    }
    // Se for um nome, retornar as iniciais
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase().slice(0, 2);
    }
    return name.substring(0, 2).toUpperCase();
  };


  // Carregar dados dos contatos (statusatd, assigned_to, name) a partir dos chats já carregados.
  // Prioriza o cache ["contacts-unified"] já populado pelo hook, evitando busca duplicada ao banco.
  useEffect(() => {
    if (!currentOrg?.id || !chats.length) return;

    const getKey = (c: any) => (c?.phone ?? c?.origem_contato ?? c?.contact_id ?? "") as string;

    // Chave de deduplicação para não processar o mesmo conjunto de chats duas vezes
    const phonesKey = chats.map(getKey).filter(Boolean).sort().join(',');
    if (refreshTrigger === undefined || refreshTrigger === 0) {
      if (processedChatsRef.current.has(phonesKey)) return;
    } else {
      processedChatsRef.current.clear();
    }
    processedChatsRef.current.add(phonesKey);
    if (processedChatsRef.current.size > 10) {
      const arr = Array.from(processedChatsRef.current);
      processedChatsRef.current = new Set([arr[arr.length - 1]]);
    }

    // Ler contatos do cache React Query (populado por useInstagramChats sem nova requisição)
    const cachedContacts: any[] = queryClient.getQueryData(["contacts-unified", currentOrg.id]) ?? [];
    const contactByPhoneOrId = new Map<string, any>();
    cachedContacts.forEach((c: any) => {
      if (c.id) contactByPhoneOrId.set(c.id, c);
      if (c.phone) { contactByPhoneOrId.set(String(c.phone).trim(), c); contactByPhoneOrId.set(normalizePhone(String(c.phone)), c); }
      if (c.whatsapp) { contactByPhoneOrId.set(String(c.whatsapp).trim(), c); }
      if (c.Account_ID_insta != null && String(c.Account_ID_insta).trim() !== '') contactByPhoneOrId.set(String(c.Account_ID_insta).trim(), c);
    });

    // Resolver nomes de usuário atribuídos (busca só se houver IDs não cacheados)
    const assignedUserIds = [...new Set(chats.map((c: any) => c.user_atribuido).filter(Boolean))] as string[];
    const resolveNames = async () => {
      let userNamesMap: Record<string, string> = {};
      if (assignedUserIds.length > 0) {
        const { data: profiles } = await supabase.from('profiles').select('id, name').in('id', assignedUserIds);
        profiles?.forEach((p: any) => { userNamesMap[p.id] = p.name; });
      }

      const contactsMap: Record<string, { statusatd?: string; assigned_to?: string; name?: string; user_name_insta?: string; assigned_to_name?: string; closed_at?: string; closed_by?: string }> = {};
      chats.forEach((chat: any) => {
        const key = getKey(chat);
        if (!key) return;
        const uid = chat.user_atribuido ?? undefined;
        const contact = (chat.contact_id ? contactByPhoneOrId.get(chat.contact_id) : undefined)
          ?? contactByPhoneOrId.get(String(chat.phone ?? chat.origem_contato ?? '').trim());
        const entry: any = {
          statusatd: chat.status_atd ?? 'fila humana',
          assigned_to: uid,
          assigned_to_name: uid ? userNamesMap[uid] : undefined,
          name: contact?.name ?? chat.name ?? chat.origem_contato ?? chat.phone,
          user_name_insta: contact?.User_name_insta ?? undefined,
          closed_at: undefined,
          closed_by: undefined,
        };
        contactsMap[key] = entry;
        const norm = typeof key === 'string' && /^\d/.test(key) ? normalizePhone(key) : key;
        if (norm && norm !== key) contactsMap[norm] = entry;
      });
      setContactsData(contactsMap);
    };

    resolveNames().catch(err => console.error('Erro ao resolver nomes de atribuição:', err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chats, currentOrg?.id, refreshTrigger]);

  // Avatar na lista: foto real se existir, senão iniciais com cor gerada pelo nome
  const ChatAvatar = React.memo(({ chat, name }: {
    chat: ChatWithMessages;
    name: string;
  }) => {
    const contact = (chat as any).full?.contact ?? (chat as any).contact;
    const pictureB64Raw = contact?.picture_b64;
    const isValidPicture = pictureB64Raw && typeof pictureB64Raw === "string" && !pictureB64Raw.startsWith("{");
    const pictureB64 = isValidPicture ? pictureB64Raw : null;

    const { bg, text } = getAvatarColor(name);
    const initials = getInitials(name);

    if (pictureB64) {
      const src = pictureB64.startsWith("data:") || pictureB64.startsWith("http")
        ? pictureB64
        : `data:image/jpeg;base64,${pictureB64}`;
      return (
        <div className="relative shrink-0 h-11 w-11 rounded-full overflow-hidden flex-shrink-0">
          <img
            src={src}
            alt={`Avatar de ${name}`}
            className="h-full w-full object-cover"
            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
          />
        </div>
      );
    }

    return (
      <div
        className="relative shrink-0 h-11 w-11 rounded-full flex items-center justify-center flex-shrink-0 text-[13px] font-bold select-none"
        style={{ background: bg, color: text }}
      >
        {initials}
      </div>
    );
  }, (prev, next) => (
    prev.chat.id === next.chat.id &&
    prev.name === next.name &&
    (prev.chat as any).full?.contact?.picture_b64 === (next.chat as any).full?.contact?.picture_b64
  ));

  // Função para atribuir contato para um usuário específico
  const handleAssignToUser = async (phone: string, userId: string, userName?: string) => {
    if (!currentOrg?.id || !phone) return;

    setLoadingAssignment(phone);

    try {
      const normalizedPhone = normalizePhone(phone);

      const chat = chats.find(c => c.phone === phone || (c as any).origem_contato === phone);
      if (chat && (chat as any).chat_id && (chat as any).instance_id) {
        const { error: chatErr } = await chatSupabase
          .from('conversations')
          .upsert({
            chat_id: (chat as any).chat_id,
            instance_id: (chat as any).instance_id,
            org_id: currentOrg.id,
            origem_contato: (chat as any).origem_contato ?? phone,
            user_atribuido: userId,
            momento_atribuido: new Date().toISOString(),
          } as any, { onConflict: 'chat_id' });
        if (chatErr) console.warn('Erro ao atualizar chats_Instagram (atribuição):', chatErr);
      }

      const now = new Date();
      const formattedDate = format(now, "dd/MM/yyyy", { locale: ptBR });
      const formattedTime = format(now, "HH:mm", { locale: ptBR });
      const systemMsg = `Este chat foi atribuído para ${userName || 'um operador'} em ${formattedDate} às ${formattedTime}`;
      if (chat?.instance_id && (chat as any).chat_id) {
        await chatSupabase.from('messages').insert({
          message_id: crypto.randomUUID(),
          chat_id: (chat as any).chat_id,
          instance_id: chat.instance_id,
          origem_contato: (chat as any).origem_contato ?? phone,
          content_message: systemMsg,
          message_type: 'text',
          user_sender: 'Sistema',
          is_internal: false,
          created_at: now.toISOString(),
        });
      }

      processedChatsRef.current.clear();
      const updatedContactData = {
        statusatd: 'em atendimento' as const,
        assigned_to: userId,
        name: (chat as any)?.name ?? phone,
        assigned_to_name: userName,
        closed_at: undefined,
        closed_by: undefined
      };
      setContactsData(prev => {
        const newData = { ...prev };
        Object.keys(prev).forEach(originalPhone => {
          if (normalizePhone(originalPhone) === normalizedPhone) newData[originalPhone] = updatedContactData;
        });
        newData[phone] = updatedContactData;
        chats.forEach(c => {
          if (c.phone && normalizePhone(c.phone) === normalizedPhone && !newData[c.phone]) newData[c.phone] = updatedContactData;
        });
        return newData;
      });

      toast.success(`Chat atribuído para ${userName || 'operador'} com sucesso!`);

      supabase.rpc('insert_user_event', {
        p_event_type: 'chat_assigned',
        p_org_id:     currentOrg.id,
        p_user_id:    user?.id ?? null,
        p_metadata:   {
          assigned_to_id:   userId,
          assigned_to_name: userName ?? null,
          assigned_by_id:   user?.id ?? null,
          chat_id:          (chat as any)?.chat_id ?? null,
          phone,
        },
      }).then(({ error }) => { if (error) console.error('[events] chat_assigned:', error) });
    } catch (error) {
      console.error('Erro ao atribuir contato:', error);
      toast.error('Erro ao atribuir contato. Tente novamente.');
    } finally {
      setLoadingAssignment(null);
    }
  };

  // Função para atribuir contato para si mesmo
  const handleAssignToMe = async (phone: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Evitar selecionar o chat ao clicar
    
    if (!user || !currentOrg?.id || !phone) return;

    setLoadingAssignment(phone);

    try {
      const normalizedPhone = normalizePhone(phone);

      const chatForAssign = chats.find(c => c.phone === phone || (c as any).origem_contato === phone);
      if (chatForAssign && (chatForAssign as any).user_atribuido === user.id) {
        toast.info('Este chat já está atribuído para você.');
        setLoadingAssignment(null);
        return;
      }
      if (chatForAssign && (chatForAssign as any).user_atribuido) {
        toast.warning('Este chat já está atribuído para outro usuário.');
        setLoadingAssignment(null);
        return;
      }

      if (chatForAssign && (chatForAssign as any).chat_id && (chatForAssign as any).instance_id) {
        const { error: chatErr } = await chatSupabase
          .from('conversations')
          .upsert({
            chat_id: (chatForAssign as any).chat_id,
            instance_id: (chatForAssign as any).instance_id,
            org_id: currentOrg.id,
            origem_contato: (chatForAssign as any).origem_contato ?? phone,
            user_atribuido: user.id,
            momento_atribuido: new Date().toISOString(),
          } as any, { onConflict: 'chat_id' });
        if (chatErr) {
          console.warn('Erro ao atualizar chats_Instagram:', chatErr);
          toast.error('Erro ao atribuir. Tente novamente.');
          setLoadingAssignment(null);
          return;
        }
      }

      const chat = chats.find(c => c.phone === phone);
      const chatId = (chat as any)?.chat_id ?? (chat as any)?.id ?? (chat as any)?.full?.chat_id;

      // Atribuição na tabela unificada (chats_Instagram + chat_messages_instagram) para qualquer canal com chat_id
      if (chatId && chat?.instance_id) {
        const now = new Date();
        const origemContato = (chat as any)?.phone ?? (chat as any)?.full?.origem_contato;
        {
          const { error: assignError } = await chatSupabase
            .from('conversations')
            .upsert({
              chat_id: chatId,
              instance_id: chat.instance_id,
              org_id: currentOrg?.id,
              origem_contato: origemContato ?? null,
              user_atribuido: user.id,
              momento_atribuido: now.toISOString(),
            } as any, { onConflict: 'chat_id' });
          if (assignError) throw assignError;

          const { data: userProfile } = await supabase
            .from('profiles')
            .select('name')
            .eq('id', user.id)
            .single();
          const userName = userProfile?.name || user?.email || 'Usuário';
          const formattedDate = format(now, "dd/MM/yyyy", { locale: ptBR });
          const formattedTime = format(now, "HH:mm", { locale: ptBR });
          await chatSupabase
            .from('messages')
            .insert({
              chat_id: chatId,
              instance_id: chat.instance_id,
              origem_contato: origemContato ?? chat.phone,
              content_message: `Este chat foi atribuído para ${userName} em ${formattedDate} às ${formattedTime}`,
              message_type: 'text',
              user_sender: 'Sistema',
              is_internal: false,
              created_at: now.toISOString(),
            });
        }

        toast.success('Contato atribuído para você com sucesso!');

        supabase.rpc('insert_user_event', {
          p_event_type: 'chat_assigned',
          p_org_id:     currentOrg.id,
          p_user_id:    user.id,
          p_metadata:   {
            assigned_to_id:   user.id,
            assigned_to_name: userProfile?.name ?? null,
            assigned_by_id:   user.id,
            chat_id:          chatId ?? null,
            phone,
          },
        }).then(({ error }) => { if (error) console.error('[events] chat_assigned:', error) });

        onContactAssigned?.();
        setLoadingAssignment(null);
        return;
      }

      // Mensagem de sistema em chat_messages_instagram quando chat não tem chat_id (fallback por instance_id + origem)
      if (chat?.instance_id && !chatId) {
        const { data: userProfile } = await supabase.from('profiles').select('name').eq('id', user.id).single();
        const userName = userProfile?.name || user?.email || 'Usuário';
        const now = new Date();
        const formattedDate = format(now, "dd/MM/yyyy", { locale: ptBR });
        const formattedTime = format(now, "HH:mm", { locale: ptBR });
        await chatSupabase.from('messages').insert({
          message_id: crypto.randomUUID(),
          instance_id: chat.instance_id,
          origem_contato: (chat as any).origem_contato ?? normalizedPhone ?? phone,
          content_message: `Este chat foi atribuído para ${userName} em ${formattedDate} às ${formattedTime}`,
          message_type: 'text',
          user_sender: 'Sistema',
          is_internal: false,
          created_at: now.toISOString(),
        });
      }

      processedChatsRef.current.clear();
      const { data: userProfileForState } = await supabase.from('profiles').select('name').eq('id', user.id).single();
      const assignedToName = userProfileForState?.name;
      const updatedContactData = {
        statusatd: 'em atendimento' as const,
        assigned_to: user.id,
        name: (chat as any)?.name ?? phone,
        assigned_to_name: assignedToName
      };
      setContactsData(prev => {
        const newData = { ...prev };
        Object.keys(prev).forEach(originalPhone => {
          if (normalizePhone(originalPhone) === normalizedPhone) newData[originalPhone] = updatedContactData;
        });
        newData[phone] = updatedContactData;
        chats.forEach(c => {
          if (c.phone && normalizePhone(c.phone) === normalizedPhone && !newData[c.phone]) newData[c.phone] = updatedContactData;
        });
        return newData;
      });

      toast.success('Contato atribuído para você com sucesso!');

      supabase.rpc('insert_user_event', {
        p_event_type: 'chat_assigned',
        p_org_id:     currentOrg.id,
        p_user_id:    user.id,
        p_metadata:   {
          assigned_to_id:   user.id,
          assigned_to_name: assignedToName ?? null,
          assigned_by_id:   user.id,
          chat_id:          null,
          phone,
        },
      }).then(({ error }) => { if (error) console.error('[events] chat_assigned:', error) });

      onContactAssigned?.();
    } catch (error) {
      console.error('Erro ao atribuir contato:', error);
      toast.error('Erro ao atribuir contato. Tente novamente.');
    } finally {
      setLoadingAssignment(null);
    }
  };

  // Helper: closed_at do Instagram pode estar em chat ou chat.full
  const getInstagramClosedAt = (chat: any) =>
    (chat?.closed_at != null && chat?.closed_at !== undefined)
      ? chat.closed_at
      : (chat?.full?.closed_at != null && chat?.full?.closed_at !== undefined)
        ? chat.full.closed_at
        : null;

  // Helper: status_atd do Instagram (chats_Instagram) — "bot" = ainda no bot, não mostrar em "Todos"
  const getInstagramStatusAtd = (chat: any) =>
    (chat?.status_atd != null && chat?.status_atd !== undefined)
      ? String(chat.status_atd).toLowerCase()
      : (chat?.full?.status_atd != null && chat?.full?.status_atd !== undefined)
        ? String(chat.full.status_atd).toLowerCase()
        : null;

  // Helper: usuário atribuído do Instagram (chats_Instagram.user_atribuido)
  const getInstagramAssignedToId = (chat: any) =>
    (chat?.user_atribuido != null && chat?.user_atribuido !== undefined)
      ? String(chat.user_atribuido)
      : (chat?.full?.user_atribuido != null && chat?.full?.user_atribuido !== undefined)
        ? String(chat.full.user_atribuido)
        : null;

  // Chave única do chat para listagem (phone, origem_contato ou contact_id — evita sumir clientes sem phone)
  const getChatKey = (c: any) => (c?.phone ?? (c as any)?.origem_contato ?? (c as any)?.contact_id ?? (c as any)?.chat_id ?? c?.id ?? "");

  // Mapa de contactsData indexado por telefone normalizado para lookups O(1) em vez de O(n)
  const normalizedContactsMap = useMemo(() => {
    const map = new Map<string, typeof contactsData[string]>();
    for (const [key, value] of Object.entries(contactsData)) {
      map.set(key, value);
      const norm = normalizePhone(key);
      if (norm && !map.has(norm)) map.set(norm, value);
    }
    return map;
  }, [contactsData]);

  const lookupContactData = (key: string) => {
    return contactsData[key] ?? normalizedContactsMap.get(normalizePhone(key)) ?? null;
  };

  // Filtrar chats baseado no filtro selecionado, busca e ordenar por mais recente primeiro
  const filteredChats = useMemo(() => {
    let filtered = [...chats]; // Criar cópia para não mutar o array original

    // REGRA IMPORTANTE: Usuários não-Admin só devem ver chats atribuídos a eles
    // Admins podem ver todos os chats (incluindo não atribuídos)
    if (!canViewAllChats && user) {
      filtered = filtered.filter(chat => {
        if (!getChatKey(chat)) return false;

        // Lista unificada: atribuição em chats_Instagram (user_atribuido) para todos os canais
        const assignedToId = getInstagramAssignedToId(chat);
        if (assignedToId != null) {
          return !!user?.id && assignedToId === user.id;
        }
        const chatKey = getChatKey(chat);
        const contactData = lookupContactData(chatKey);
        if (contactData?.assigned_to === user.id) {
          return true;
        }
        
        // 2. Chats que eles finalizaram nos últimos 30 dias (mesmo que não estejam mais atribuídos)
        if (contactData?.closed_by === user.id && contactData?.closed_at) {
          const closedAt = new Date(contactData.closed_at);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          
          // Se foi finalizado nos últimos 30 dias, mostrar
          if (closedAt >= thirtyDaysAgo) {
            return true;
          }
        }
        
        // Caso contrário, não mostrar
        return false;
      });
    }

    // Se showClosedChats estiver ativo, mostrar APENAS chats encerrados (unificado: closed_at em chats_Instagram)
    if (showClosedChats) {
      filtered = filtered.filter(chat => {
        const closedAt = getInstagramClosedAt(chat);
        if (closedAt != null) return true;
        const statusAtdClosed = getInstagramStatusAtd(chat);
        if (statusAtdClosed === 'chat finalizado' || statusAtdClosed === 'chat encerrado') return true;
        const closedChatKey = getChatKey(chat);
        if (!closedChatKey) return false;
        const contactData = lookupContactData(closedChatKey);
        return contactData?.statusatd === 'chat finalizado' || contactData?.statusatd === 'Chat Encerrado';
      });
    } else {
      // "Chats do Bot": abertos (sem closed_at) com status_atd = 'bot'
      if (filterType === "in_bot") {
        filtered = filtered.filter(chat => {
          const closedAt = getInstagramClosedAt(chat);
          if (closedAt) return false;
          const statusAtd = getInstagramStatusAtd(chat);
          return statusAtd === 'bot';
        });
      } else {
        // "Todos": abertos (sem closed_at) que NÃO estão no bot (admins/owners veem conversas do bot)
        filtered = filtered.filter(chat => {
          const closedAt = getInstagramClosedAt(chat);
          if (closedAt) return false;
          const statusAtd = getInstagramStatusAtd(chat);
          if (statusAtd === 'bot' && !isAdmin) return false;
          return true;
        });
      }
    }

    // Aplicar filtro por tipo (all, assigned, unassigned, assigned_to_me) - apenas se showClosedChats estiver desativado e não for "Chats do Bot"
    if (!showClosedChats && filterType !== "in_bot") {
      if (filterType === "unassigned") {
        filtered = filtered.filter(chat => !getInstagramAssignedToId(chat));
      } else if (filterType === "assigned") {
        filtered = filtered.filter(chat => !!getInstagramAssignedToId(chat));
      } else if (filterType === "assigned_to_me") {
        filtered = filtered.filter(chat => !!user?.id && getInstagramAssignedToId(chat) === user.id);
      }
    }

    // Aplicar filtros avançados
    // Filtro por status de atendimento (fonte: chat.status_atd ou contactData)
    if (advancedFilters.statusFilter !== "all") {
      filtered = filtered.filter(chat => {
        const statusFromChat = getInstagramStatusAtd(chat);
        let status = statusFromChat ?? "fila humana";
        const statusKey = getChatKey(chat);
        if (status === "fila humana" && statusKey) {
          const contactData = lookupContactData(statusKey);
          if (contactData?.statusatd) status = contactData.statusatd;
        }
        if (advancedFilters.statusFilter === "em_atendimento") {
          return status === "em atendimento";
        } else if (advancedFilters.statusFilter === "fila_humana") {
          return status === "fila humana";
        } else if (advancedFilters.statusFilter === "finalizado") {
          return status === "finalizado" || (status?.toLowerCase() || '').includes("finalizado");
        }
        return true;
      });
    }

    // Filtro por data
    if (advancedFilters.dateFilter !== "all") {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const thisWeek = new Date(today);
      thisWeek.setDate(today.getDate() - 7);
      const thisMonth = new Date(today);
      thisMonth.setMonth(today.getMonth() - 1);

      filtered = filtered.filter(chat => {
        const lastMessageDate = chat.lastMessage?.created_at 
          ? new Date(chat.lastMessage.created_at)
          : chat.updated_at 
            ? new Date(chat.updated_at)
            : chat.created_at 
              ? new Date(chat.created_at)
              : null;
        
        if (!lastMessageDate) return false;

        if (advancedFilters.dateFilter === "today") {
          return lastMessageDate >= today;
        } else if (advancedFilters.dateFilter === "this_week") {
          return lastMessageDate >= thisWeek;
        } else if (advancedFilters.dateFilter === "this_month") {
          return lastMessageDate >= thisMonth;
        } else if (advancedFilters.dateFilter === "custom") {
          if (customDateStart && customDateEnd) {
            const start = new Date(customDateStart);
            start.setHours(0, 0, 0, 0);
            const end = new Date(customDateEnd);
            end.setHours(23, 59, 59, 999);
            return lastMessageDate >= start && lastMessageDate <= end;
          } else if (customDateStart) {
            const start = new Date(customDateStart);
            start.setHours(0, 0, 0, 0);
            return lastMessageDate >= start;
          }
        }
        return true;
      });
    }

    // Filtro por chats fechados/abertos (Instagram: chat.closed_at ou chat.full.closed_at; WhatsApp: contactData.closed_at)
    if (advancedFilters.closedFilter !== "all") {
      filtered = filtered.filter(chat => {
        const isInstagramChat = chat.channel === 'instagram';
        const closedKey = getChatKey(chat);
        const isClosed = isInstagramChat
          ? !!getInstagramClosedAt(chat)
          : (() => {
              if (!closedKey) return false;
              const contactData = lookupContactData(closedKey);
              return (contactData as any)?.closed_at != null && (contactData as any)?.closed_at !== undefined;
            })();
        if (advancedFilters.closedFilter === "closed") {
          return isClosed;
        } else if (advancedFilters.closedFilter === "open") {
          return !isClosed;
        }
        return true;
      });
    }

    // Filtro por tags (múltiplas)
    if (advancedFilters.selectedTags.length > 0) {
      filtered = filtered.filter(chat => {
        const tagKey = getChatKey(chat);
        if (!tagKey) return false;
        const chatTags = contactTags[tagKey] || [];
        const chatTagIds = chatTags.map((tag: any) => String(tag.id));
        // O chat deve ter pelo menos uma das tags selecionadas
        return advancedFilters.selectedTags.some(tagId => chatTagIds.includes(tagId));
      });
    }

    // Aplicar filtro de busca se houver searchQuery
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(chat => {
        const searchKey = getChatKey(chat);
        const contactData = searchKey ? lookupContactData(searchKey) : null;
        
        const contactName = contactData?.name || "";
        const phone = searchKey || "";
        const lastMessage = chat.lastMessage?.user_message || chat.lastMessage?.bot_message || "";
        
        // Buscar no nome (normalizado e original)
        const nameMatch = contactName.toLowerCase().includes(query) || 
                         normalizePhone(contactName).includes(query);
        
        // Buscar no telefone/identificador (normalizado e original)
        const phoneMatch = phone.toLowerCase().includes(query) || 
                          normalizePhone(phone).includes(query);
        
        // Buscar na última mensagem
        const messageMatch = lastMessage.toLowerCase().includes(query);
        
        // Buscar nas tags
        const chatTags = searchKey ? (contactTags[searchKey] || []) : [];
        const tagsMatch = chatTags.some((tag: any) => 
          (tag.name?.toLowerCase() || '').includes(query)
        );
        
        return nameMatch || phoneMatch || messageMatch || tagsMatch;
      });
    }

    // Garantir que o chat selecionado sempre apareça na lista, mesmo que os filtros o excluam.
    // Isso evita que o chat suma da sidebar enquanto o usuário está respondendo.
    // IMPORTANTE: usar chat_id/id (UUID único) e NÃO phone, pois dois chats diferentes (ex.: WebChat
    // e WhatsApp do mesmo contato) podem ter o mesmo número de telefone. Usar phone causaria falso positivo
    // no "já está na lista" e o chat não seria readicionado.
    if (selectedChat) {
      const selectedId = (selectedChat as any).id || (selectedChat as any).chat_id;
      const alreadyInList = selectedId
        ? filtered.some(c => {
            const cId = (c as any).id || (c as any).chat_id;
            return cId != null && String(cId) === String(selectedId);
          })
        : filtered.some(c => getChatKey(c) === getChatKey(selectedChat));
      if (!alreadyInList) {
        filtered.push(selectedChat);
      }
    }

    // Ordenar por mais recente primeiro (baseado em updated_at ou lastMessage.created_at)
    filtered.sort((a, b) => {
      const aTime = a.lastMessage?.created_at || (a as any).updated_at || (a as any).created_at || '';
      const bTime = b.lastMessage?.created_at || (b as any).updated_at || (b as any).created_at || '';
      return new Date(bTime).getTime() - new Date(aTime).getTime();
    });

    return filtered;
  }, [chats, filterType, contactsData, normalizedContactsMap, searchQuery, contactTags, advancedFilters, user, customDateStart, customDateEnd, canViewAllChats, showClosedChats, selectedChat]);

  return (
    <div className="flex flex-col h-full bg-background w-full max-w-full overflow-hidden">
      {/* Aviso se a migration não foi aplicada */}
      {hasColumnError && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 m-4 mb-0">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Atenção:</strong> Execute a migration do banco de dados para habilitar os filtros de designação.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Header da lista */}
      <div className="px-4 pt-4 md:pt-8 pb-0 bg-white/95 flex-shrink-0 sticky top-0 z-10">
        {/* Título + botões */}
        <div className="flex items-center gap-2 pb-3">
          <h2 className="flex-1 font-semibold text-[17px] md:text-[20px] leading-5 md:leading-6 text-[#020817] tracking-[-0.4px] md:tracking-[-0.6px]">
            Chats
          </h2>

          {/* Status do operador */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="bg-[#eef1f4] hover:bg-[#e2e6ea] rounded-[12px] px-3 py-2 text-[13px] font-medium text-[#4a5565] flex items-center gap-1.5 shrink-0 transition-colors">
                <span
                  className="h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: OPERATOR_STATUS_CONFIG[operatorStatus].color }}
                />
                {OPERATOR_STATUS_CONFIG[operatorStatus].label}
                <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {(Object.entries(OPERATOR_STATUS_CONFIG) as [OperatorStatus, typeof OPERATOR_STATUS_CONFIG[OperatorStatus]][]).map(([key, cfg]) => (
                <DropdownMenuItem
                  key={key}
                  onClick={() => onOperatorStatusChange(key)}
                  className={cn("gap-2", operatorStatus === key && "font-semibold")}
                >
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: cfg.color }} />
                  {cfg.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Busca */}
          <button
            onClick={() => { mixpanel.track('app_chats_search_toggled', { open: !isSearchFocused }); setIsSearchFocused(!isSearchFocused); if (!isSearchFocused) setTimeout(() => searchInputRef.current?.focus(), 50); }}
            className="bg-[#eef1f4] hover:bg-[#e2e6ea] rounded-full h-10 w-10 flex items-center justify-center shrink-0 transition-colors"
          >
            <Search className="h-4 w-4 text-[#4a5565]" />
          </button>

          {/* Nova conversa */}
          <button
            onClick={() => { mixpanel.track('app_chats_new_conversation_clicked'); setShowNewConversationDialog(true) }}
            disabled={!selectedInstance}
            className="bg-[#2563EB] hover:bg-[#1d4ed8] rounded-full h-10 w-10 flex items-center justify-center shrink-0 text-white shadow-[0_4px_14px_rgba(37,99,235,0.5)] transition-colors disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        {/* Search input (colapsável) */}
        {isSearchFocused && (
          <div className="mb-3 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94a3b8] pointer-events-none" />
            <Input
              ref={searchInputRef}
              placeholder="Buscar por nome ou número..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10 h-10 bg-[#eef1f4] border-0 rounded-xl focus-visible:ring-1 focus-visible:ring-[#2563EB]"
              onBlur={() => { if (!searchQuery) setIsSearchFocused(false); }}
            />
          </div>
        )}

        {/* Tabs de filtro */}
        <div className="flex gap-0 border-b border-[#e2e8f0] overflow-x-auto scrollbar-hide">
          {(() => {
            const activeTab = showClosedChats ? "finalizados" : filterType === "in_bot" ? "bot" : filterType === "assigned_to_me" ? "meus" : "todos";
            const tabClass = (tab: string) => cn(
              "pb-2.5 px-3 text-[13px] font-medium border-b-2 whitespace-nowrap transition-colors shrink-0",
              activeTab === tab
                ? "border-[#2563EB] text-[#2563EB]"
                : "border-transparent text-[#64748b] hover:text-[#020817]"
            );
            return (
              <>
                <button className={tabClass("todos")} onClick={() => { mixpanel.track('app_chats_filter_changed', { filter: 'all' }); setFilterType("all"); setShowClosedChats(false); }}>Todos</button>
                {isAdmin && <button className={tabClass("meus")} onClick={() => { mixpanel.track('app_chats_filter_changed', { filter: 'assigned_to_me' }); setFilterType("assigned_to_me"); setShowClosedChats(false); }}>Meus</button>}
                {isAdmin && <button className={tabClass("bot")} onClick={() => { mixpanel.track('app_chats_filter_changed', { filter: 'in_bot' }); setFilterType("in_bot"); setShowClosedChats(false); }}>Bot</button>}
                <button className={tabClass("finalizados")} onClick={() => { mixpanel.track('app_chats_filter_changed', { filter: 'finalizados', show: true }); setShowClosedChats(true); setFilterType("all"); }}>Finalizados</button>
              </>
            );
          })()}
        </div>
      </div>

      {/* Lista de chats */}
      <ScrollArea className="flex-1 overflow-hidden" style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
        {isLoading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Carregando chats...</p>
          </div>
        ) : filteredChats.length === 0 ? (
          <div className="p-6 text-center">
            <MessageSquare className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              {searchQuery 
                ? "Nenhuma conversa encontrada" 
                : filterType === "unassigned"
                  ? "Nenhum contato não designado"
                  : filterType === "assigned"
                    ? "Nenhum contato designado"
                    : filterType === "in_bot"
                      ? "Nenhum chat no fluxo do bot no momento"
                      : "Nenhuma conversa ainda"}
            </p>
          </div>
        ) : (
          <div className="flex flex-col bg-white">
            {filteredChats.map((chat) => {
              const channelInfo = getChatChannelInfo(chat);
              const isInstagramChannel = channelInfo?.name === 'Instagram';

              const contactData = chat.phone ? lookupContactData(chat.phone) : null;
              
              // Status de atribuição (Instagram usa chats_Instagram.user_atribuido)
              const assignedToId = getInstagramAssignedToId(chat) ?? contactData?.assigned_to;
              const isUnassigned = !assignedToId;
              const isAssignedToMe = !!user?.id && assignedToId === user.id;
              const assignedToName = !assignedToId
                ? undefined
                : isInstagramChannel
                  ? (organizationMembers.find(m => m.id === assignedToId)?.name ||
                     organizationMembers.find(m => m.id === assignedToId)?.email ||
                     'Usuário')
                  : (contactData?.assigned_to_name || 'Usuário');
              
              // Nome do cliente: adapter (contactName/contactHandle), chat.name, contactData
              let fullClientName = "Não identificado";
              const adapterName = (chat as any).contactName;
              const adapterHandle = (chat as any).contactHandle;
              if (adapterName && String(adapterName).trim() !== "") {
                fullClientName = String(adapterName).trim();
              } else if (adapterHandle && String(adapterHandle).trim() !== "") {
                fullClientName = (String(adapterHandle).trim().startsWith("@") ? "" : "@") + String(adapterHandle).trim().replace(/^@/, "");
              } else if (chat.name && chat.name.trim() !== "") {
                fullClientName = chat.name;
              } else if (contactData?.name && contactData.name.trim() !== "" && contactData.name !== chat.phone && normalizePhone(contactData.name) !== normalizePhone(chat.phone || "")) {
                fullClientName = contactData.name;
              } else if (contactData?.user_name_insta && contactData.user_name_insta.trim() !== "" && contactData.user_name_insta !== chat.phone) {
                fullClientName = `@${contactData.user_name_insta.replace(/^@/, "")}`;
              }
              const clientName = fullClientName.length > 22
                ? fullClientName.substring(0, 22) + '...'
                : fullClientName;
              
              const instagramUsernameRaw = contactData?.user_name_insta?.trim();
              const instagramUsername = instagramUsernameRaw
                ? `@${instagramUsernameRaw.replace(/^@/, '')}`
                : undefined;
              
              // Obter última mensagem e limitar a 20 caracteres
              // Verificar se a última mensagem é uma mídia (imagem, áudio, vídeo ou arquivo)
              const lastMessageHasMedia = chat.lastMessage && (
                (chat.lastMessage as any).media_type ||
                (chat.lastMessage as any).media_file_name
              );
              
              let fullLastMessage = "";
              if (lastMessageHasMedia) {
                // Se é mídia, criar mensagem descritiva baseada no tipo
                const mediaType = (chat.lastMessage as any).media_type || '';
                const mediaFileName = (chat.lastMessage as any).media_file_name || '';
                
                if (mediaType.startsWith('image/') || mediaType === 'image') {
                  fullLastMessage = '📷 Imagem';
                } else if (mediaType.startsWith('video/') || mediaType === 'video') {
                  fullLastMessage = '🎥 Vídeo';
                } else if (mediaType.startsWith('audio/') || mediaType === 'audio') {
                  fullLastMessage = '🎵 Áudio';
                } else if (mediaType === 'document' || mediaFileName) {
                  fullLastMessage = `📎 ${mediaFileName || 'Arquivo'}`;
                } else {
                  fullLastMessage = '📎 Arquivo';
                }
              } else {
                // Texto: Instagram usa content_message; WhatsApp usa user_message/bot_message
                const lastMsg = chat.lastMessage as any;
                fullLastMessage =
                  lastMsg?.content_message ||
                  chat.lastMessage?.user_message ||
                  chat.lastMessage?.bot_message ||
                  (chat.lastMessage ? "Mensagem" : "Sem mensagens");
              }
              
              const lastMessage = fullLastMessage.length > 45
                ? fullLastMessage.substring(0, 45) + '...'
                : fullLastMessage;

              // Detectar se a última mensagem foi enviada pelo operador (para mostrar ✓✓)
              const isUuidStr = (v: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
              const lastMsgRaw = chat.lastMessage as any;
              const lastMsgSender = String(lastMsgRaw?.user_sender ?? '').trim();
              const lastMsgAgentId = String(lastMsgRaw?.agent_id ?? '').trim();
              const isLastMsgFromOperator = !!(lastMsgRaw && (
                isUuidStr(lastMsgAgentId) ||
                isUuidStr(lastMsgSender) ||
                lastMsgRaw?.sender_type === 'operator' ||
                lastMsgRaw?.sender_type === 'bot'
              ));

              // Tags do contato
              const chatTagsKey = getChatKey(chat);
              const chatTagsList: any[] = chatTagsKey
                ? (contactTags[chatTagsKey] || contactTags[normalizePhone(chatTagsKey)] || [])
                : [];

              const hasUnread = chat.unreadCount > 0;

              return (
                <div
                  key={chat.id}
                  onClick={() => onChatSelect(chat)}
                  onMouseEnter={() => onPrefetchChat?.(chat)}
                  className={cn(
                    "flex items-start gap-[11px] px-[14px] py-[10px] cursor-pointer transition-colors border-b border-[#f1f5f9]",
                    selectedChat?.id === chat.id
                      ? "bg-[#eff2ff]"
                      : "hover:bg-[#f7f8fa]"
                  )}
                >
                  <ChatAvatar chat={chat} name={fullClientName} />

                  <div className="flex flex-col gap-[3px] flex-1 min-w-0">
                    {/* Linha 1: Nome + Horário */}
                    <div className="flex items-baseline justify-between gap-1">
                      <p
                        className="font-semibold text-[13.5px] leading-[18px] text-[#0f172a] truncate"
                        title={fullClientName.length > 22 ? fullClientName : undefined}
                      >
                        {clientName}
                      </p>
                      {chat.lastMessage && (
                        <span className={cn(
                          "text-[11.5px] whitespace-nowrap shrink-0 font-medium",
                          hasUnread ? "text-[#2554ef]" : "text-[#94a3b8]"
                        )}>
                          {formatTime(chat.lastMessage.created_at || "")}
                        </span>
                      )}
                    </div>

                    {/* Linha 2: Preview + Badge não lido */}
                    <div className="flex items-center justify-between gap-1">
                      <p className={cn(
                        "text-[12.5px] leading-[18px] truncate flex-1",
                        hasUnread ? "text-[#334155] font-medium" : "text-[#64748b]"
                      )}>
                        {isLastMsgFromOperator && !hasUnread && (
                          <span className="text-[#2554ef] mr-0.5 text-[11px]">✓✓ </span>
                        )}
                        {lastMessage}
                      </p>
                      {hasUnread && (
                        <span className="bg-[#2554ef] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center shrink-0">
                          {chat.unreadCount > 99 ? "99+" : chat.unreadCount}
                        </span>
                      )}
                    </div>

                    {/* Linha 3: Chips canal + atribuição + tags */}
                    <div className="flex items-center gap-1 flex-wrap mt-[2px]">
                      {channelInfo && (
                        <span className={cn(
                          "inline-flex items-center gap-[3px] rounded-full h-[17px] px-[6px] text-[10px] font-semibold whitespace-nowrap",
                          channelInfo.badgeBg, channelInfo.badgeText
                        )}>
                          {channelInfo.icon && <channelInfo.icon className="w-2.5 h-2.5" />}
                          {channelInfo.name}
                        </span>
                      )}
                      {isUnassigned && (
                        <span className="inline-flex items-center rounded-full h-[17px] px-[6px] bg-[#f1f5f9] text-[#64748b] text-[10px] font-medium whitespace-nowrap border border-[#e2e8f0]">
                          Não designado
                        </span>
                      )}
                      {isAssignedToMe && (
                        <span className="inline-flex items-center rounded-full h-[17px] px-[6px] bg-[#eff2ff] text-[#2554ef] text-[10px] font-semibold whitespace-nowrap">
                          Você
                        </span>
                      )}
                      {!isAssignedToMe && assignedToId && (
                        <span className="inline-flex items-center rounded-full h-[17px] px-[6px] bg-[#f1f5f9] text-[#475569] text-[10px] font-medium max-w-[90px] truncate" title={assignedToName || 'Usuário'}>
                          {assignedToName || 'Usuário'}
                        </span>
                      )}
                      {chatTagsList.slice(0, 2).map((tag: any) => (
                        <span
                          key={tag.id}
                          className="inline-flex items-center gap-[3px] rounded-full h-[17px] px-[6px] text-[10px] font-medium whitespace-nowrap"
                          style={{ background: `${tag.color}20`, color: tag.color }}
                          title={tag.name}
                        >
                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: tag.color }} />
                          {tag.name.length > 9 ? tag.name.substring(0, 9) + '…' : tag.name}
                        </span>
                      ))}
                      {chatTagsList.length > 2 && (
                        <span className="inline-flex items-center rounded-full h-[17px] px-[6px] bg-[#f1f5f9] text-[#64748b] text-[10px] font-medium">
                          +{chatTagsList.length - 2}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>

      {/* Dialog para Nova Conversa */}
      {/* Modal de Nova Conversa */}
      <NewConversationDialog
        open={showNewConversationDialog}
        onOpenChange={setShowNewConversationDialog}
        selectedInstance={selectedInstance}
        onSuccess={onRefetchChats}
      />
    </div>
  );
}
